// User management utilities with password and email support
const USER_STORAGE_KEY = 'chess_user_accounts';
const USER_SESSIONS_KEY = 'chess_user_sessions';

// Import role management
import { setUserRole, ROLES } from './roleManager';

// Default user account structure
const createDefaultUserAccount = (username, password, email = null) => ({
  username,
  password, // In production, this should be hashed
  email,
  createdAt: Date.now(),
  lastLogin: null,
  isActive: true,
  profileComplete: true,
  preferences: {
    theme: 'default',
    notifications: true,
    privacy: 'public'
  }
});

// Get user accounts data
export const getUserAccountsData = () => {
  try {
    const data = localStorage.getItem(USER_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error loading user accounts:', error);
    return {};
  }
};

// Save user accounts data
export const saveUserAccountsData = (data) => {
  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving user accounts:', error);
    return false;
  }
};

// Check if username exists
export const userExists = (username) => {
  const accounts = getUserAccountsData();
  return Object.prototype.hasOwnProperty.call(accounts, username);
};

// Check if email exists
export const emailExists = (email) => {
  if (!email) return false;
  const accounts = getUserAccountsData();
  return Object.values(accounts).some(account => account.email === email);
};

// Create new user account
export const createUserAccount = (username, password, email = null) => {
  try {
    if (userExists(username)) {
      return { success: false, error: 'Username already exists' };
    }

    if (email && emailExists(email)) {
      return { success: false, error: 'Email already exists' };
    }

    const accounts = getUserAccountsData();
    accounts[username] = createDefaultUserAccount(username, password, email);
    
    if (saveUserAccountsData(accounts)) {
      // Set default user role
      setUserRole(username, ROLES.USER.id);
      return { success: true, user: accounts[username] };
    }

    return { success: false, error: 'Failed to save user account' };
  } catch (error) {
    console.error('Error creating user account:', error);
    return { success: false, error: 'Failed to create account' };
  }
};

// Authenticate user
export const authenticateUser = (username, password) => {
  try {
    const accounts = getUserAccountsData();
    const account = accounts[username];
    
    if (!account) {
      return { success: false, error: 'User not found' };
    }

    if (!account.isActive) {
      return { success: false, error: 'Account is disabled' };
    }

    if (account.password !== password) {
      return { success: false, error: 'Invalid password' };
    }

    // Update last login
    account.lastLogin = Date.now();
    accounts[username] = account;
    saveUserAccountsData(accounts);

    return { success: true, user: account };
  } catch (error) {
    console.error('Error authenticating user:', error);
    return { success: false, error: 'Authentication failed' };
  }
};

// Update user account
export const updateUserAccount = (username, updates) => {
  try {
    const accounts = getUserAccountsData();
    const account = accounts[username];
    
    if (!account) {
      return { success: false, error: 'User not found' };
    }

    // Validate email uniqueness if updating email
    if (updates.email && updates.email !== account.email && emailExists(updates.email)) {
      return { success: false, error: 'Email already exists' };
    }

    accounts[username] = { ...account, ...updates };
    
    if (saveUserAccountsData(accounts)) {
      return { success: true, user: accounts[username] };
    }

    return { success: false, error: 'Failed to update account' };
  } catch (error) {
    console.error('Error updating user account:', error);
    return { success: false, error: 'Failed to update account' };
  }
};

// Change user password
export const changeUserPassword = (username, oldPassword, newPassword) => {
  try {
    const accounts = getUserAccountsData();
    const account = accounts[username];
    
    if (!account) {
      return { success: false, error: 'User not found' };
    }

    if (account.password !== oldPassword) {
      return { success: false, error: 'Current password is incorrect' };
    }

    account.password = newPassword;
    accounts[username] = account;
    
    if (saveUserAccountsData(accounts)) {
      return { success: true };
    }

    return { success: false, error: 'Failed to update password' };
  } catch (error) {
    console.error('Error changing password:', error);
    return { success: false, error: 'Failed to change password' };
  }
};

// Get user account
export const getUserAccount = (username) => {
  const accounts = getUserAccountsData();
  return accounts[username] || null;
};

// Delete user account
export const deleteUserAccount = (username) => {
  try {
    const accounts = getUserAccountsData();
    if (accounts[username]) {
      delete accounts[username];
      return saveUserAccountsData(accounts);
    }
    return false;
  } catch (error) {
    console.error('Error deleting user account:', error);
    return false;
  }
};

// Get current logged in user
export const getCurrentUser = () => {
  return sessionStorage.getItem('current_user') || localStorage.getItem('chess_player_name');
};

// Set current logged in user
export const setCurrentUser = (username) => {
  sessionStorage.setItem('current_user', username);
  localStorage.setItem('chess_player_name', username);
};

// Logout current user
export const logoutCurrentUser = () => {
  sessionStorage.removeItem('current_user');
  // Keep chess_player_name for compatibility but could be removed later
};

// Check if user is logged in
export const isUserLoggedIn = () => {
  const username = getCurrentUser();
  if (!username) return false;
  
  const account = getUserAccount(username);
  return account && account.isActive;
};

// Validate password strength
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters long');
  }
  
  if (!/[A-Za-z]/.test(password)) {
    errors.push('Password must contain at least one letter');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate email format
export const validateEmail = (email) => {
  if (!email) return { isValid: true, errors: [] }; // Email is optional
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(email);
  
  return {
    isValid,
    errors: isValid ? [] : ['Please enter a valid email address']
  };
};

// Validate username
export const validateUsername = (username) => {
  const errors = [];
  
  if (!username || username.trim().length < 2) {
    errors.push('Username must be at least 2 characters long');
  }
  
  if (username.length > 20) {
    errors.push('Username must be less than 20 characters');
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    errors.push('Username can only contain letters, numbers, hyphens, and underscores');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Admin function to create user with role
export const adminCreateUser = (adminUsername, userData) => {
  const { username, password, email, role } = userData;
  
  // Validate admin permissions would be checked in the calling component
  
  const result = createUserAccount(username, password, email);
  
  if (result.success && role) {
    setUserRole(username, role);
  }
  
  return result;
};

// Get all user accounts (admin function)
export const getAllUserAccounts = () => {
  return getUserAccountsData();
};

// Initialize user management system
export const initializeUserSystem = () => {
  try {
    // Migrate existing players to user accounts if needed
    const existingPlayer = localStorage.getItem('chess_player_name');
    if (existingPlayer && !userExists(existingPlayer)) {
      // Create account for existing player without password (legacy support)
      createUserAccount(existingPlayer, '', null);
    }
    
    console.log('User management system initialized');
    return true;
  } catch (error) {
    console.error('Error initializing user system:', error);
    return false;
  }
};